"use client";

import * as React from "react";
import { Star, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = React.useRef<T | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  React.useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  author: "Elena M.",
  position: "Spine Surgery Patient",
  quote: "They didn't just treat my back; they treated me like their own sister. The minimally invasive approach had me home in days, feeling stronger than ever.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/pnw_forest_mist_blurred.png"
}, {
  author: "David R.",
  position: "Brain Health Patient",
  quote: "NeuroFamily's team combined world-class surgical precision with a warmth I didn't think possible in a clinical setting. I felt safe every step of the way.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/pnw_coast_fog_blurred.png"
}, {
  author: "Linda K.",
  position: "Nerve Recovery Patient",
  quote: "The multidisciplinary care meant every specialist was on the same page. I felt seen, heard, and truly cared for as a person, not just a diagnosis.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603830/img/pnw_mountain_lake_blurred.png"
}];
export default function PatientSuccessStories() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section data-section-id="102965" id="voices-of-recovery" className="bg-background py-24 md:py-32 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-sans font-bold text-primary mb-6 tracking-tighter">
            Voices of Recovery
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Experience the journeys of those we treat as family. Our multidisciplinary approach and minimally invasive focus provide a compassionate path back to your life.
          </p>
        </div>

        <div className="relative mx-auto max-w-5xl">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i}>
                  <div className="relative overflow-hidden rounded-3xl aspect-[4/5] md:aspect-[21/9] flex items-center justify-center p-6 md:p-12 lg:p-20 group">
                    
                    {}
                    <img src={testimonial.image} alt={`Abstract background for ${testimonial.author}'s recovery journey`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-primary/30 to-primary/70 mix-blend-multiply" />
                    
                    {}
                    <div className="relative z-10 w-full max-w-3xl mx-auto bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-8 md:p-12 shadow-xl flex flex-col items-center text-center">
                      
                      <Quote className="size-10 text-chart-2/40 mb-6 rotate-180" />
                      
                      <Badge variant="secondary" className="mb-8 gap-1 bg-accent/50 text-accent-foreground backdrop-blur-md border-border py-1.5 px-3">
                        {[...Array(5)].map((_, starIndex) => <span key={starIndex} data-index={starIndex}>
                            <Star className="size-3.5 fill-chart-2 text-chart-2" />
                          </span>)}
                      </Badge>
                      
                      <blockquote className="text-xl md:text-3xl lg:text-4xl font-serif italic text-foreground leading-snug mb-10">
                        "{testimonial.quote}"
                      </blockquote>
                      
                      <div className="mt-auto">
                        <div className="font-bold text-primary text-lg font-sans">
                          {testimonial.author}
                        </div>
                        <div className="text-sm text-chart-2 uppercase tracking-widest mt-1 font-sans font-medium">
                          {testimonial.position}
                        </div>
                      </div>
                    </div>

                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="flex justify-center items-center gap-3 mt-10">
            {Array.from({
            length: count
          }).map((_, i) => <button key={i} data-index={i} onClick={() => api?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`} className={`transition-all duration-300 ease-in-out rounded-full ${current === i ? "w-8 h-2.5 bg-chart-2 shadow-sm" : "w-2.5 h-2.5 bg-chart-2/30 hover:bg-chart-2/50"}`} />)}
          </div>

        </div>
      </div>
    </section>;
}